import { ChangeDetectionStrategy, Component, Input, OnInit, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { DashboardService, LinkStatsApi, RecentLinkApi } from '../dashboard.service';
import { PatientLink } from '../interfaces/dashboard.component';
import { TranslateService } from '@ngx-translate/core';

type LinkFilter = 'all' | 'opened' | 'unopened';

@Component({
  selector: 'app-dashboard-patient-links',
  standalone: false,
  templateUrl: './dashboard-patient-links.html',
  styleUrl: './dashboard-patient-links.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPatientLinksComponent implements OnInit {
  @Input() loading = false;

  readonly linksPeriod = signal<'today' | 'week' | 'month'>('week');
  readonly linkSummary = signal<{ sent: number; opened: number; unopened: number }>({
    sent: 0,
    opened: 0,
    unopened: 0,
  });

  private readonly rawLinks = signal<RecentLinkApi[]>([]);
  private readonly lang = signal('');
  readonly patientLinks = computed<PatientLink[]>(() => {
    this.lang();
    return this.mapPatientLinks(this.rawLinks());
  });

  readonly filter = signal<LinkFilter>('all');
  readonly filteredLinks = computed<PatientLink[]>(() => {
    const f = this.filter();
    const links = this.patientLinks();
    if (f === 'opened') return links.filter((l) => l.status === 'viewed');
    if (f === 'unopened') return links.filter((l) => l.status === 'sent');
    return links;
  });

  readonly copiedUrl = signal<string | null>(null);

  readonly loadingLinks = signal(true);

  readonly openRate = computed(() => {
    const { sent, opened } = this.linkSummary();
    if (sent === 0) return null;
    return Math.round((opened / sent) * 100);
  });

  readonly linksPeriodLabel = computed(() => {
    const p = this.linksPeriod();
    if (p === 'today') return this.translate.instant('GUI.dashboard.selects.today');
    if (p === 'week') return this.translate.instant('GUI.dashboard.selects.this week');
    return this.translate.instant('GUI.dashboard.selects.this month');
  });

  constructor(
    private dashboardService: DashboardService,
    private translate: TranslateService
  ) {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe((e) => this.lang.set(e.lang));
  }

  ngOnInit(): void {
    this.dashboardService.getLinkStats('week').subscribe({
      next: (data) => this.applyLinkStats(data),
      error: () => this.loadingLinks.set(false),
    });
  }

  onLinksPeriodChange(period: string): void {
    const p = period as 'today' | 'week' | 'month';
    this.linksPeriod.set(p);
    this.filter.set('all');
    this.loadingLinks.set(true);
    this.dashboardService.getLinkStats(p).subscribe({
      next: (data) => this.applyLinkStats(data),
      error: () => this.loadingLinks.set(false),
    });
  }

  setFilter(f: LinkFilter): void {
    this.filter.set(this.filter() === f ? 'all' : f);
  }

  copyLink(event: Event, url: string, tip?: MatTooltip): void {
    event.preventDefault();
    navigator.clipboard.writeText(url).then(() => {
      this.copiedUrl.set(url);
      tip?.show();
      setTimeout(() => {
        this.copiedUrl.set(null);
        tip?.hide();
      }, 2000);
    });
  }

  private applyLinkStats(data: LinkStatsApi): void {
    this.loadingLinks.set(false);
    this.linkSummary.set({
      sent: data.linksSent,
      opened: data.linksOpened,
      unopened: data.linksUnopened,
    });
    this.rawLinks.set(data.recentLinks);
  }

  private mapPatientLinks(data: RecentLinkApi[]): PatientLink[] {
    const sent = this.translate.instant('GUI.dashboard.labels.sent');
    const unopened = this.translate.instant('GUI.dashboard.labels.chips.unopened');
    const viewed = this.translate.instant('GUI.dashboard.labels.viewed');
    return data.map((l) => ({
      name: `${l.First} ${l.Last}`,
      status: l.Views > 0 ? ('viewed' as const) : ('sent' as const),
      text: l.Views > 0 ? `${viewed} ${l.Views}×` : `${sent} · ${unopened}`,
      type: l.LinkType,
      url: l.url,
    }));
  }
}
